/**
 * Signup Steps Configuration
 *
 * This file defines the multi-step signup flow for each user type.
 * Each user type has a sequence of steps with specific fields to collect.
 *
 * Step Structure:
 * - id: Unique identifier for the step
 * - titleKey: Translation key for step title
 * - stepNumber: Display number (shown as "Step X")
 * - fields: Array of field names to collect in this step
 * - submitKey: Translation key for the submit button
 * - isAccountCreation: If true, this step creates the account
 */

// Step IDs
export const STEP_EMAIL = 'email';
export const STEP_PASSWORD = 'password';
export const STEP_PROFILE = 'profile';
export const STEP_VERIFY = 'verify';

// Audience signup steps
export const AUDIENCE_STEPS = [
  {
    id: STEP_EMAIL,
    titleKey: null, // Uses dynamic title based on user type
    stepNumber: 0,
    fields: ['email'],
    submitKey: 'SignupWizard.nextButton',
    showSocialLogins: true,
    showLoginLink: true,
  },
  {
    id: STEP_PASSWORD,
    titleKey: 'SignupWizard.passwordStepTitle',
    stepNumber: 1,
    fields: ['password'],
    submitKey: 'SignupWizard.nextButton',
    showSocialLogins: false,
    showLoginLink: false,
  },
  {
    id: STEP_PROFILE,
    titleKey: 'SignupWizard.profileStepTitle',
    stepNumber: 2,
    fields: ['firstName', 'lastName', 'ageConfirmed'],
    submitKey: 'SignupWizard.signUpButton',
    isAccountCreation: true,
    showSocialLogins: false,
    showLoginLink: false,
  },
  {
    id: STEP_VERIFY,
    titleKey: 'SignupWizard.verifyStepTitle',
    stepNumber: 3,
    fields: [],
    submitKey: 'SignupWizard.resendVerification',
    isPostSignup: true,
    showSocialLogins: false,
    showLoginLink: false,
  },
];

// Creator signup steps
export const CREATOR_STEPS = [
  {
    id: STEP_EMAIL,
    titleKey: null,
    stepNumber: 0,
    fields: ['email'],
    submitKey: 'SignupWizard.nextButton',
    showSocialLogins: true,
    showLoginLink: true,
  },
  {
    id: STEP_PASSWORD,
    titleKey: 'SignupWizard.passwordStepTitle',
    stepNumber: 1,
    fields: ['password'],
    submitKey: 'SignupWizard.nextButton',
    showSocialLogins: false,
    showLoginLink: false,
  },
  {
    id: STEP_PROFILE,
    titleKey: 'SignupWizard.creatorProfileStepTitle',
    stepNumber: 2,
    fields: ['firstName', 'lastName', 'ageConfirmed', 'phoneNumber'],
    submitKey: 'SignupWizard.signUpButton',
    isAccountCreation: true,
    showSocialLogins: false,
    showLoginLink: false,
  },
  {
    id: STEP_VERIFY,
    titleKey: 'SignupWizard.verifyStepTitle',
    stepNumber: 3,
    fields: [],
    submitKey: 'SignupWizard.resendVerification',
    isPostSignup: true,
    showSocialLogins: false,
    showLoginLink: false,
  },
];

// Community-only mode signup steps (simplified)
export const COMMUNITY_AUDIENCE_STEPS = [
  {
    id: STEP_EMAIL,
    titleKey: null,
    stepNumber: 0,
    fields: ['email'],
    submitKey: 'SignupWizard.nextButton',
    showSocialLogins: false,
    showLoginLink: true,
  },
  {
    id: STEP_PASSWORD,
    titleKey: 'SignupWizard.passwordStepTitle',
    stepNumber: 1,
    fields: ['password'],
    submitKey: 'SignupWizard.nextButton',
    showSocialLogins: false,
    showLoginLink: false,
  },
  {
    id: STEP_PROFILE,
    titleKey: 'SignupWizard.profileStepTitle',
    stepNumber: 2,
    fields: ['firstName', 'lastName', 'ageConfirmed'], // NO phone
    submitKey: 'SignupWizard.signUpButton',
    isAccountCreation: true,
    showSocialLogins: false,
    showLoginLink: false,
  },
  {
    id: STEP_VERIFY,
    titleKey: 'SignupWizard.verifyStepTitle',
    stepNumber: 3,
    fields: [],
    submitKey: 'SignupWizard.resendVerification',
    isPostSignup: true,
    showSocialLogins: false,
    showLoginLink: false,
  },
];

// Organization signup steps
export const ORGANIZATION_STEPS = [
  {
    id: STEP_EMAIL,
    titleKey: null,
    stepNumber: 0,
    fields: ['email'],
    submitKey: 'SignupWizard.nextButton',
    showSocialLogins: true,
    showLoginLink: true,
  },
  {
    id: STEP_PASSWORD,
    titleKey: 'SignupWizard.passwordStepTitle',
    stepNumber: 1,
    fields: ['password'],
    submitKey: 'SignupWizard.nextButton',
    showSocialLogins: false,
    showLoginLink: false,
  },
  {
    id: STEP_PROFILE,
    titleKey: 'SignupWizard.organizationProfileStepTitle',
    stepNumber: 2,
    fields: ['firstName', 'lastName', 'organizationName', 'ageConfirmed', 'phoneNumber'],
    submitKey: 'SignupWizard.signUpButton',
    isAccountCreation: true,
    showSocialLogins: false,
    showLoginLink: false,
  },
  {
    id: STEP_VERIFY,
    titleKey: 'SignupWizard.verifyStepTitle',
    stepNumber: 3,
    fields: [],
    submitKey: 'SignupWizard.resendVerification',
    isPostSignup: true,
    showSocialLogins: false,
    showLoginLink: false,
  },
];

/**
 * Get signup steps for a specific user role
 * @param {string} role - User role (audience, creator, organization)
 * @returns {Array} Array of step configurations
 */
export const getSignupStepsForRole = role => {
  // In community-only mode, always use simplified steps
  if (process.env.REACT_APP_BETA_MODE === 'true') {
    return COMMUNITY_AUDIENCE_STEPS;
  }

  switch (role) {
    case 'audience':
      return AUDIENCE_STEPS;
    case 'creator':
      return CREATOR_STEPS;
    case 'organization':
      return ORGANIZATION_STEPS;
    default:
      return AUDIENCE_STEPS;
  }
};

/**
 * Get total number of steps for progress calculation (excluding email step)
 * @param {string} role - User role
 * @returns {number} Total number of visible steps
 */
export const getTotalSteps = role => {
  const steps = getSignupStepsForRole(role);
  // Count steps that have a stepNumber > 0 (visible in progress)
  return steps.filter(step => step.stepNumber > 0).length;
};

/**
 * Get progress percentage for a given step
 * @param {number} currentStepNumber - Current step number (1-based)
 * @param {number} totalSteps - Total number of steps
 * @returns {number} Progress percentage (0-100)
 */
export const getProgressPercentage = (currentStepNumber, totalSteps) => {
  if (currentStepNumber === 0 || totalSteps === 0) return 0;
  return Math.round((currentStepNumber / totalSteps) * 100);
};
